// projectWorkflowReportIndex.js
// Pure builders for PROJECT collection Overview reports (workflow-sourced).

import {
  formatDurationMinutes,
  getMissionCompletionElapsedMs,
  MS_PER_MINUTE,
  resolveEstimatedDurationMinutes,
} from 'missions/scheduling/missionScheduleUtils';
import {
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
} from 'missions/taskmodal/missionTaskModalUtils';
import {
  getMissionActivePathTasks,
  isMissionRunComplete,
} from 'node/missions/mission/utils/missionRunUtils';
import { getNodelistNodeTitle } from 'utilities/NodelistField';

export const OPEN_STEP_STATUSES = Object.freeze([
  'ready',
  'inProgress',
  'running',
  'blocked',
]);

const OPEN_STEP_STATUS_SET = new Set(OPEN_STEP_STATUSES);

export const UNASSIGNED_ASSIGNEE_KEY = '__unassigned__';

export const PROJECT_WORKFLOW_AGING_SORT = Object.freeze({
  AGE: 'ageMs',
  ASSIGNEE: 'assigneeLabel',
  OVERDUE: 'overdue',
  PROJECT: 'projectTitle',
  STATUS: 'status',
  STEP: 'stepTitle',
  WORKFLOW: 'workflowTitle',
});

export const PROJECT_WORKLOAD_SORT = Object.freeze({
  NAME: 'name',
  OPEN: 'openCount',
  OVERDUE: 'overdueCount',
  PROJECTS: 'projectCount',
});

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
function isReportableProjectNode(node) {
  if (!node || typeof node !== 'object') return false;
  if (node.isCatalogPlaceholder === true) return false;
  return true;
}

/**
 * @param {number} value
 * @returns {number}
 */
function safeMs(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

/**
 * @param {number[]} values
 * @returns {number|null}
 */
export function medianNumber(values) {
  const list = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!list.length) return null;
  const mid = Math.floor(list.length / 2);
  if (list.length % 2 === 1) return list[mid];
  return (list[mid - 1] + list[mid]) / 2;
}

/**
 * @param {number[]} values
 * @param {number} percentile - 0..100
 * @returns {number|null}
 */
export function percentileNumber(values, percentile) {
  const list = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!list.length) return null;
  const clamped = Math.max(0, Math.min(100, Number(percentile) || 0));
  if (list.length === 1) return list[0];
  const rank = (clamped / 100) * (list.length - 1);
  const low = Math.floor(rank);
  const high = Math.ceil(rank);
  if (low === high) return list[low];
  const weight = rank - low;
  return (list[low] * (1 - weight)) + (list[high] * weight);
}

/**
 * @param {number|null|undefined} ms
 * @returns {string}
 */
export function formatAgeLabel(ms) {
  const minutes = Math.round(Number(ms || 0) / MS_PER_MINUTE);
  if (!Number.isFinite(minutes) || minutes < 0) return '—';
  if (minutes < 1) return '<1 min';
  return formatDurationMinutes(minutes) || '—';
}

/**
 * @param {object|null|undefined} run
 * @returns {string}
 */
export function resolveRunOutcomeKey(run) {
  const tasks = Array.isArray(run?.tasks) ? run.tasks : [];
  let outcome = 'complete';
  tasks.forEach((task) => {
    if (String(task?.status || '').trim() !== 'complete') return;
    const key = String(task?.completionOutcomeKey || '').trim();
    if (key) outcome = key;
  });
  return outcome;
}

/**
 * @param {object|null|undefined} run
 * @returns {'complete'|'inProgress'|'notStarted'}
 */
export function resolveRunLifecycle(run) {
  if (!run || typeof run !== 'object') return 'notStarted';
  if (isMissionRunComplete(run)) return 'complete';
  const tasks = Array.isArray(run?.tasks) ? run.tasks : [];
  if (!tasks.length) return 'notStarted';
  const hasStarted = tasks.some((task) => {
    const status = String(task?.status || '').trim();
    return status && status !== 'waiting';
  });
  return hasStarted ? 'inProgress' : 'notStarted';
}

/**
 * @param {object} task
 * @param {number} nowMs
 * @returns {{ ageMs: number, allottedMinutes: number, deadlineMs: number, overdue: boolean }}
 */
export function computeOpenStepTiming(task, nowMs = Date.now()) {
  const readyAtMs = safeMs(task?.readyAtMs);
  const allottedMinutes = resolveEstimatedDurationMinutes(task);
  const ageMs = readyAtMs > 0 ? Math.max(0, Number(nowMs) - readyAtMs) : 0;
  const deadlineMs = (
    readyAtMs > 0 && allottedMinutes > 0
      ? readyAtMs + (allottedMinutes * MS_PER_MINUTE)
      : 0
  );
  const overdue = deadlineMs > 0 && Number(nowMs) > deadlineMs;

  return {
    ageMs,
    allottedMinutes,
    deadlineMs,
    overdue,
  };
}

/**
 * Builds flat open-step + run-summary indexes from PROJECT collection nodes.
 *
 * @param {object} params
 * @param {object[]} params.nodes
 * @param {number} [params.nowMs]
 * @returns {{ openSteps: object[], runSummaries: object[], templateOptions: object[], assigneeOptions: object[] }}
 */
export function buildProjectWorkflowReportIndex({
  nodes = [],
  nowMs = Date.now(),
} = {}) {
  const openSteps = [];
  const runSummaries = [];
  const templateMap = new Map();
  const assigneeMap = new Map();

  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    if (!isReportableProjectNode(node)) return;

    const projectKey = String(node?.key || '').trim();
    if (!projectKey) return;

    const projectTitle = getNodelistNodeTitle(node);
    const missionRuns = node?.missionRuns && typeof node.missionRuns === 'object'
      ? node.missionRuns
      : {};

    Object.entries(missionRuns).forEach(([templateKeyRaw, run]) => {
      const templateKey = String(templateKeyRaw || '').trim();
      if (!templateKey || !run || typeof run !== 'object') return;

      const workflowTitle = String(run?.templateTitle || '').trim() || templateKey;
      templateMap.set(templateKey, workflowTitle);

      const lifecycle = resolveRunLifecycle(run);
      const launchedAtMs = safeMs(run?.launchedAtMs);
      const completed = lifecycle === 'complete';
      const cycleTimeMs = completed ? getMissionCompletionElapsedMs(run) : 0;
      const outcomeKey = completed ? resolveRunOutcomeKey(run) : '';

      runSummaries.push({
        completed,
        cycleTimeMs: cycleTimeMs > 0 ? cycleTimeMs : null,
        launchedAtMs,
        lifecycle,
        outcomeKey,
        projectKey,
        projectTitle,
        templateKey,
        workflowTitle,
      });

      const pathTasks = getMissionActivePathTasks(run);
      pathTasks.forEach((task) => {
        const status = String(task?.status || '').trim();
        if (!OPEN_STEP_STATUS_SET.has(status)) return;

        const stepKey = String(task?.key || '').trim();
        if (!stepKey) return;

        const timing = computeOpenStepTiming(task, nowMs);
        const assignees = Array.isArray(task?.assignees) ? task.assignees : [];
        const assigneeEntries = assignees
          .map((person) => {
            const key = getMissionTaskPersonKey(person);
            if (!key) return null;
            const label = getMissionTaskPersonLabel(person) || key;
            const coverUrl = String(person?.coverUrl || '').trim();
            assigneeMap.set(key, { coverUrl, key, label });
            return { coverUrl, key, label };
          })
          .filter(Boolean);

        if (!assigneeEntries.length) {
          assigneeMap.set(UNASSIGNED_ASSIGNEE_KEY, {
            coverUrl: '',
            key: UNASSIGNED_ASSIGNEE_KEY,
            label: 'Unassigned',
          });
        }

        openSteps.push({
          ageMs: timing.ageMs,
          allottedMinutes: timing.allottedMinutes,
          assigneeKeys: assigneeEntries.length
            ? assigneeEntries.map((entry) => entry.key)
            : [UNASSIGNED_ASSIGNEE_KEY],
          assigneeLabel: assigneeEntries.length
            ? assigneeEntries.map((entry) => entry.label).join(', ')
            : 'Unassigned',
          assignees: assigneeEntries,
          deadlineMs: timing.deadlineMs,
          overdue: timing.overdue,
          overdueMs: timing.overdue && timing.deadlineMs > 0
            ? Math.max(0, Number(nowMs) - timing.deadlineMs)
            : 0,
          projectKey,
          projectTitle,
          readyAtMs: safeMs(task?.readyAtMs),
          status,
          stepKey,
          stepTitle: String(task?.title || '').trim() || stepKey,
          templateKey,
          workflowTitle,
        });
      });
    });
  });

  const templateOptions = Array.from(templateMap.entries())
    .map(([value, label]) => ({ label, value }))
    .sort((left, right) => left.label.localeCompare(right.label));

  const assigneeOptions = Array.from(assigneeMap.values())
    .sort((left, right) => {
      if (left.key === UNASSIGNED_ASSIGNEE_KEY) return 1;
      if (right.key === UNASSIGNED_ASSIGNEE_KEY) return -1;
      return left.label.localeCompare(right.label);
    });

  return {
    assigneeOptions,
    openSteps,
    runSummaries,
    templateOptions,
  };
}

/**
 * @param {object[]} openSteps
 * @param {object} [filters]
 * @returns {object[]}
 */
export function filterOpenSteps(openSteps, {
  assigneeKey = '',
  templateKey = '',
} = {}) {
  const cleanAssignee = String(assigneeKey || '').trim();
  const cleanTemplate = String(templateKey || '').trim();

  return (Array.isArray(openSteps) ? openSteps : []).filter((step) => {
    if (cleanTemplate && step.templateKey !== cleanTemplate) return false;
    if (cleanAssignee) {
      const keys = Array.isArray(step.assigneeKeys) ? step.assigneeKeys : [];
      if (!keys.includes(cleanAssignee)) return false;
    }
    return true;
  });
}

/**
 * @param {object[]} openSteps
 * @param {object} [options]
 * @returns {object[]}
 */
export function buildStageAgingRows(openSteps, {
  assigneeKey = '',
  sortDirection = 'desc',
  sortKey = PROJECT_WORKFLOW_AGING_SORT.OVERDUE,
  templateKey = '',
} = {}) {
  const filtered = filterOpenSteps(openSteps, { assigneeKey, templateKey });
  const direction = sortDirection === 'asc' ? 1 : -1;

  return filtered.slice().sort((left, right) => {
    if (sortKey === PROJECT_WORKFLOW_AGING_SORT.OVERDUE) {
      if (left.overdue !== right.overdue) {
        // Desc: overdue first. Asc: on-time first.
        const overdueFirst = left.overdue ? -1 : 1;
        return sortDirection === 'asc' ? -overdueFirst : overdueFirst;
      }
      return (left.overdueMs - right.overdueMs) * direction
        || (left.ageMs - right.ageMs) * direction
        || left.projectTitle.localeCompare(right.projectTitle);
    }

    if (sortKey === PROJECT_WORKFLOW_AGING_SORT.AGE) {
      return (left.ageMs - right.ageMs) * direction
        || left.projectTitle.localeCompare(right.projectTitle);
    }

    const leftText = String(left[sortKey] || '');
    const rightText = String(right[sortKey] || '');
    return leftText.localeCompare(rightText) * direction
      || (right.ageMs - left.ageMs);
  });
}

/**
 * @param {object[]} openSteps
 * @param {object} [options]
 * @returns {object[]}
 */
export function buildWorkloadRows(openSteps, {
  sortDirection = 'desc',
  sortKey = PROJECT_WORKLOAD_SORT.OVERDUE,
  templateKey = '',
} = {}) {
  const filtered = filterOpenSteps(openSteps, { templateKey });
  const byAssignee = new Map();

  filtered.forEach((step) => {
    const keys = Array.isArray(step.assigneeKeys) ? step.assigneeKeys : [UNASSIGNED_ASSIGNEE_KEY];
    keys.forEach((assigneeKey) => {
      const person = (Array.isArray(step.assignees) ? step.assignees : [])
        .find((entry) => entry.key === assigneeKey);
      const label = assigneeKey === UNASSIGNED_ASSIGNEE_KEY
        ? 'Unassigned'
        : (person?.label || assigneeKey);
      const coverUrl = person?.coverUrl || '';

      if (!byAssignee.has(assigneeKey)) {
        byAssignee.set(assigneeKey, {
          coverUrl,
          key: assigneeKey,
          name: label,
          openCount: 0,
          overdueCount: 0,
          projectKeys: new Set(),
        });
      }

      const row = byAssignee.get(assigneeKey);
      row.openCount += 1;
      if (step.overdue) row.overdueCount += 1;
      row.projectKeys.add(step.projectKey);
      if (!row.coverUrl && coverUrl) row.coverUrl = coverUrl;
      if (label && row.name === assigneeKey) row.name = label;
    });
  });

  const direction = sortDirection === 'asc' ? 1 : -1;
  return Array.from(byAssignee.values())
    .map((row) => ({
      coverUrl: row.coverUrl,
      key: row.key,
      name: row.name,
      openCount: row.openCount,
      overdueCount: row.overdueCount,
      projectCount: row.projectKeys.size,
    }))
    .sort((left, right) => {
      if (sortKey === PROJECT_WORKLOAD_SORT.NAME) {
        if (left.key === UNASSIGNED_ASSIGNEE_KEY) return 1;
        if (right.key === UNASSIGNED_ASSIGNEE_KEY) return -1;
        return left.name.localeCompare(right.name) * direction;
      }
      const leftValue = Number(left[sortKey] || 0);
      const rightValue = Number(right[sortKey] || 0);
      if (leftValue !== rightValue) return (leftValue - rightValue) * direction;
      return left.name.localeCompare(right.name);
    });
}

/**
 * @param {number|null|undefined} ms
 * @param {Date|null|undefined} rangeStart
 * @param {Date|null|undefined} rangeEnd
 * @returns {boolean}
 */
export function isTimestampInInclusiveRange(ms, rangeStart, rangeEnd) {
  const value = safeMs(ms);
  if (!value || !rangeStart || !rangeEnd) return false;
  const start = rangeStart instanceof Date ? rangeStart.getTime() : 0;
  const end = rangeEnd instanceof Date ? rangeEnd.getTime() : 0;
  if (!start || !end || end < start) return false;
  // Inclusive calendar-style: end of selected end day is caller's responsibility.
  return value >= start && value <= end;
}

/**
 * @param {object[]} runSummaries
 * @param {object} [options]
 * @returns {object}
 */
export function buildThroughputSummary(runSummaries, {
  rangeEnd = null,
  rangeStart = null,
  templateKey = '',
} = {}) {
  const cleanTemplate = String(templateKey || '').trim();
  const filtered = (Array.isArray(runSummaries) ? runSummaries : []).filter((run) => {
    if (cleanTemplate && run.templateKey !== cleanTemplate) return false;
    if (rangeStart || rangeEnd) {
      return isTimestampInInclusiveRange(run.launchedAtMs, rangeStart, rangeEnd);
    }
    return Boolean(run.launchedAtMs);
  });

  const launchedCount = filtered.length;
  const completedRuns = filtered.filter((run) => run.completed);
  const completedCount = completedRuns.length;
  const openCount = filtered.filter((run) => run.lifecycle === 'inProgress').length;
  const cycleTimes = completedRuns
    .map((run) => run.cycleTimeMs)
    .filter((value) => Number.isFinite(value) && value > 0);

  const outcomeCounts = new Map();
  completedRuns.forEach((run) => {
    const key = String(run.outcomeKey || 'complete').trim() || 'complete';
    outcomeCounts.set(key, (outcomeCounts.get(key) || 0) + 1);
  });

  const outcomeMix = Array.from(outcomeCounts.entries())
    .map(([key, count]) => ({
      count,
      key,
      label: key === 'complete' ? 'Complete' : key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
      ratio: completedCount > 0 ? count / completedCount : 0,
    }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));

  return {
    completedCount,
    cycleTimeMedianMs: medianNumber(cycleTimes),
    cycleTimeP90Ms: percentileNumber(cycleTimes, 90),
    launchedCount,
    openCount,
    outcomeMix,
    runCount: launchedCount,
  };
}
